// `PixelTerrain`: a board drawn through the view-tile ruleset (game/tiling) onto a plain canvas,
// for document screens that want Oryx ground under them — the title's clearing, the overworld,
// the local map — without the Phaser scene. The canvas is drawn at the atlas's native 24 px and
// scaled by CSS with `image-rendering: pixelated`, so `scale` is always a whole number.
//
// `terrainFromRows` builds a board from a character grid (the legend below), which is how the
// screens paint their maps: `.` open ground, `T` wall (trees / bushes in outdoor kits), `~` water,
// `,` rough (undergrowth, mud), `#` door, `=` tunnel.
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { kitFor, tileBoard, type BoardLike, type TiledBoard } from '../../game/tiling';
import type { Terrain, TileData } from '../../core/sim/types';

interface Frame { x: number; y: number; w: number; h: number }
let worldPromise: Promise<{ img: HTMLImageElement; frames: Record<string, Frame> } | undefined> | undefined;

/** The world atlas (image + frame rects), fetched once per page. */
export function loadWorldAtlas(): Promise<{ img: HTMLImageElement; frames: Record<string, Frame> } | undefined> {
  worldPromise ??= (async () => {
    try {
      const r = await fetch('/assets/oryx/world.json');
      if (!r.ok) return undefined;
      const j = (await r.json()) as { frames: Record<string, { frame: Frame }> };
      const img = new Image();
      img.src = '/assets/oryx/world.png';
      await img.decode();
      return { img, frames: Object.fromEntries(Object.entries(j.frames).map(([k, v]) => [k, v.frame])) };
    } catch { return undefined; }
  })();
  return worldPromise;
}

const LEGEND: Record<string, Terrain> = { '.': 'Open', T: 'Wall', '~': 'Water', ',': 'Bad', '#': 'Door', '=': 'Tunnel', _: 'Chasm' };

/** A board from a character grid (rows of equal length; unknown characters are open ground). */
export function terrainFromRows(rows: readonly string[], seed?: number): BoardLike {
  const h = rows.length, w = Math.max(0, ...rows.map((r) => r.length));
  const tiles: TileData[] = [];
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const terrain = LEGEND[rows[y]![x] ?? '.'] ?? 'Open';
    tiles.push({ terrain, elev: 0, cover: 0, opaque: terrain === 'Wall' });
  }
  return { w, h, tiles, ...(seed !== undefined ? { seed } : {}) };
}

/** Draw a tiled board into a 2D context at 1 px per atlas pixel (the second animation frame when `phase`). */
export function paintTiled(ctx: CanvasRenderingContext2D, tiled: TiledBoard, atlas: { img: CanvasImageSource; frames: Record<string, Frame> }, cell = 24, phase = 0, layers: ReadonlyArray<'ground' | 'overlay' | 'props'> = ['ground', 'overlay', 'props']): void {
  ctx.imageSmoothingEnabled = false;
  // a pass that lays the ground starts the canvas over; a props-only pass paints on what is there
  if (layers.includes('ground')) ctx.clearRect(0, 0, tiled.w * cell, tiled.h * cell);
  const draw = (name: string, x: number, y: number) => {
    const f = atlas.frames[name];
    if (f) ctx.drawImage(atlas.img, f.x, f.y, f.w, f.h, x * cell, y * cell + (cell - f.h), f.w, f.h);
  };
  for (const layer of layers) {
    for (let y = 0; y < tiled.h; y++) for (let x = 0; x < tiled.w; x++) {
      for (const v of tiled.cells[y * tiled.w + x]![layer]) draw(v.anim && phase ? v.anim[phase % v.anim.length]! : v.frame, x, y);
    }
  }
}

export interface PixelTerrainProps {
  board: BoardLike;
  /** A kit key from game/tiling/kits (forest, mirewood, autumn, keep, …). */
  kit: string;
  /** Whole-number CSS scale of the 24 px cells. */
  scale?: number;
  /** Cycle animated frames (water ripples, torches) every 650 ms. */
  animate?: boolean;
  className?: string;
  style?: CSSProperties;
  /** Called once the first frame is painted (screenshots, fades). */
  onReady?: () => void;
}

export function PixelTerrain({ board, kit, scale = 2, animate, className = '', style, onReady }: PixelTerrainProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const [phase, setPhase] = useState(0);
  const [atlas, setAtlas] = useState<Awaited<ReturnType<typeof loadWorldAtlas>>>();
  const tiledRef = useRef<{ board: BoardLike; kit: string; tiled: TiledBoard } | undefined>(undefined);
  useEffect(() => { let live = true; void loadWorldAtlas().then((a) => { if (live) setAtlas(a); }); return () => { live = false; }; }, []);
  useEffect(() => {
    if (!animate) return;
    const t = window.setInterval(() => setPhase((p) => (p + 1) % 2), 650);
    return () => window.clearInterval(t);
  }, [animate]);
  useEffect(() => {
    const c = ref.current; if (!c || !atlas) return;
    const cached = tiledRef.current;
    const tiled = cached && cached.board === board && cached.kit === kit ? cached.tiled : tileBoard(board, kitFor(kit));
    tiledRef.current = { board, kit, tiled };
    const ctx = c.getContext('2d'); if (!ctx) return;
    paintTiled(ctx, tiled, atlas, 24, phase);
    if (phase === 0) onReady?.();
  }, [atlas, board, kit, phase, onReady]);
  return (
    <canvas ref={ref} className={`fk-terrain ${className}`} width={board.w * 24} height={board.h * 24} aria-hidden="true" data-ready={atlas ? 'true' : 'false'}
      style={{ width: board.w * 24 * scale, height: board.h * 24 * scale, ...style }} />
  );
}
